import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { prop, partition } from 'ramda'
import { makeStyles } from '@material-ui/styles'
import ListMenu from '../menu/ListMenu'
import MenuItem from '../menu/MenuItem'
import { bottomRight, topMiddle } from 'core/elements/menu/defaults'

import { pathStrOr } from 'app/utils/fp'
import { clientActions, clientStoreKey, ClientState } from 'core/client/clientReducers'
import { sessionStoreKey, SessionState } from 'core/session/sessionReducers'
import FrameContext from 'core/providers/frame-provider'
import useToggler from 'core/hooks/useToggler'
import usePluginRouter from 'core/hooks/usePluginRouter'
import Theme from 'core/themes/model'
import { matchesCurrentPath } from 'core/plugins/route-helpers'
import Text from 'core/elements/Text'
import IconButton from 'core/elements/button/IconButton'
import clsx from 'clsx'
import NavItem from './NavItem'
import NavPane from './NavPane'
import SpinLogo from './SpinLogo'
import generateTestId from 'utils/test-helpers'
import { trackEvent } from 'utils/tracking'
import NavSection from './NavSection'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'

interface Props {
  setPluginId: (id: string) => void
}
const iconProps = {
  solid: true,
}

const sidebarPaneRef = React.createRef<HTMLDivElement>()
const Sidebar: FC<Props> = ({ setPluginId }) => {
  const dispatch = useDispatch()
  const [menuOpen, toggleMenuOpen] = useToggler(false)
  const { features = {} } = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { frame: { sidebarState = 'expanded', sidebarPane = 'default' } = {} } = useSelector(
    prop<string, ClientState>(clientStoreKey),
  )
  const drawerOpen = sidebarState === 'expanded'
  const version = pathStrOr('4', 'releaseVersion', features)
  const { sections, currentSection, currentPath } = usePluginRouter()
  const classes = useStyles({ drawerOpen, menuOpen })
  const [currentSectionBottomLinks, currentSectionLinks] = useMemo(
    () => partition((link) => link.isBottomLink, currentSection.links),
    [currentSection],
  )

  const toggleDrawer = useCallback(() => {
    dispatch(clientActions.setSidebarState(sidebarState === 'expanded' ? 'collapsed' : 'expanded'))
  }, [sidebarState])

  const { setFrameContainerRef } = React.useContext(FrameContext)
  useEffect(() => {
    setFrameContainerRef({
      sidebarPaneContainer: sidebarPaneRef.current,
    })
  }, [])

  const handleChange = useCallback(
    (newPluginId) => {
      setPluginId(newPluginId)
      trackEvent('Changed Application Plugin', {
        application: newPluginId,
      })
      toggleMenuOpen()
    },
    [setPluginId],
  )
  const isPluginSelectorActive = Boolean(menuOpen)
  const pluginMenuOffset = useMemo(
    () => ({
      vertical: 8,
      horizontal: drawerOpen ? -(288 / 2) : -(72 / 2),
    }),
    [drawerOpen],
  )
  const navMenuOffset = useMemo(
    () => ({
      vertical: -48,
      horizontal: 8,
    }),
    [drawerOpen],
  )

  const bottomContent = useMemo(
    () =>
      drawerOpen
        ? [
            <li key={version}>
              <Text variant="body2" component="h6" className={classes.version}>
                Version: {version}
              </Text>
            </li>,
          ]
        : currentSectionBottomLinks.map((bLink, idx) => (
            <NavItem
              {...bLink}
              key={idx}
              isActive={matchesCurrentPath(currentPath, bLink.link)}
              drawerOpen={drawerOpen}
              tooltip
              tooltipProps={{ className: classes.navText }}
            />
          )),
    [version, drawerOpen, currentPath, currentSectionBottomLinks],
  )

  return (
    <>
      <aside
        className={clsx(classes.customNav, {
          [classes.customNavWidth]: sidebarPane === 'custom',
          [classes.openPane]: sidebarPane === 'custom',
          [classes.hiddenPane]: sidebarPane !== 'custom',
        })}
      >
        <div className="sidebarPanePortal" ref={sidebarPaneRef} />
      </aside>
      <aside
        className={clsx(classes.customNav, classes.nav, {
          [classes.openPane]: sidebarPane === 'default',
          [classes.hiddenPane]: sidebarPane !== 'default',
        })}
      >
        <ListMenu
          data-testid={generateTestId('plugin', 'menu')}
          id="plugin-menu"
          list={sections}
          onClose={toggleMenuOpen}
          open={isPluginSelectorActive}
          offset={pluginMenuOffset}
          align={bottomRight.align}
          origin="left top"
          className={classes.pluginMenuContainer}
          render={(item) => (
            <MenuItem
              key={item.id}
              textVariant="subtitle2"
              onClick={() => handleChange(item.id)}
              className={classes.pluginMenuItem}
              iconProps={iconProps}
              icon={typeof item.icon === 'string' ? item.icon : undefined}
            >
              {typeof item.icon !== 'string' ? item.icon : null}
              {item.name}
            </MenuItem>
          )}
          anchor={
            <div
              className={clsx(classes.pluginSelector, { active: isPluginSelectorActive })}
              onClick={toggleMenuOpen}
            >
              <SpinLogo active={isPluginSelectorActive} />
              <Text variant="subtitle1" className={classes.pluginTitle}>
                {currentSection.name}
              </Text>
              {sidebarPane !== 'custom' && (
                <FontAwesomeIcon solid size="xs" className={classes.pluginChevron}>
                  chevron-down
                </FontAwesomeIcon>
              )}
            </div>
          }
        />
        {sidebarPane !== 'custom' && (
          <IconButton
            data-testid={generateTestId('side', 'bar', 'arrow')}
            className={classes.toggleCaret}
            onClick={toggleDrawer}
            icon={drawerOpen ? 'chevron-left' : 'chevron-right'}
            size="md"
            solid
          />
        )}
        <NavPane
          className={clsx(classes.primaryPane, 'thin-scrollbar')}
          bottomContent={bottomContent}
        >
          {currentSectionLinks?.map((navItem, idx) => (
            <NavSection
              {...navItem}
              key={`${navItem?.name}-${idx}`}
              isActive={matchesCurrentPath(currentPath, navItem?.link)}
              drawerOpen={drawerOpen}
              menuOffset={navMenuOffset}
            />
          ))}
        </NavPane>
      </aside>
    </>
  )
}

interface StyleProps {
  drawerOpen: boolean
  menuOpen: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  hiddenPane: {
    transform: ({ drawerOpen }) => `translate(-${drawerOpen ? '289' : '73'}px, 0px)`,
  },
  openPane: {
    transform: ({ drawerOpen }) => `translate(0px, 0px)`,
    backgroundColor: theme.components.sidebar.background,
  },
  customNavWidth: {
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '100%',
      bottom: 0,
      width: 128,
      backgroundColor: theme.components.sidebar.background,
    },

    '& ~section.content-main': {
      zIndex: 1000,
      padding: '80px 32px',
    },
  },
  nav: {
    position: 'relative',
    width: ({ drawerOpen }) => (drawerOpen ? 288 : 72),
    zIndex: 100,
    height: '100vh',
    borderRight: `1px solid ${theme.components.sidebar.border}`,
    backgroundColor: theme.components.sidebar.background,
    display: 'grid',
    justifyContent: 'stretch',
    transition: 'width .2s ease',
    gridTemplateRows: 'max-content 1fr',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `"nav-header" "nav-primary-pane"`,
  },
  pluginSelector: {
    display: 'grid',
    gridTemplateColumns: '72px 1fr 1fr',
    alignItems: 'center',
    justifyItems: 'stretch',
    height: 64,
    borderBottom: `1px solid ${theme.components.sidebar.border}`,
    cursor: 'pointer',
    '&:hover, &.active': {
      background: theme.components.sidebar.border,
    },
    '& #logoDefault': {
      justifySelf: 'center',
    },
  },
  pluginTitle: {
    color: theme.components.sidebar.activeText,
    textTransform: 'capitalize',
    display: ({ drawerOpen }) => (drawerOpen ? 'unset' : 'none'),
  },
  pluginChevron: {
    position: 'absolute',
    left: 250,
    display: ({ drawerOpen }) => (drawerOpen ? 'unset' : 'none'),
    color: ({ menuOpen }) => theme.components.sidebar?.[menuOpen ? 'activeIcon' : 'text'],
  },
  pluginMenuItem: {
    textTransform: 'capitalize',
    background: theme.components.sidebar.background,
    color: theme.components.sidebar.activeText,
    '&:hover': {
      background: theme.components.sidebar.border,
    },

    '& i': {
      fontSize: 22,
      width: 24,
      height: 24,
      color: theme.components.sidebar.activeText,
    },
  },
  pluginMenuContainer: {
    gridArea: 'nav-header',

    '& .menu-popover': {
      padding: 8,
      backgroundColor: theme.components.sidebar.background,
      borderColor: theme.components.sidebar.border,
    },
  },
  toggleCaret: {
    backgroundColor: theme.palette.grey['000'],
    boxShadow: '0 4px 24px 0 rgba(37, 37, 63, 0.25)',
    position: 'absolute',
    right: 0,
    top: 64 - 24 / 2,
    width: 24,
    height: 24,
    borderRadius: 24,
    border: 'none',
    transform: 'translate(50%, 0)',
    color: theme.palette.grey[900],
    outline: '0 !important',
    zIndex: 10,
    display: 'grid',
    alignItems: 'center',
    justifyItems: 'center',
    '&:hover': {
      color: theme.palette.grey[900],
      backgroundColor: theme.palette.grey[100],
    },
    '& i': {
      fontSize: 12,
      width: 'auto',
    },
  },
  primaryPane: {
    gridArea: 'nav-primary-pane',
    overflow: ({ drawerOpen }) => (drawerOpen ? 'auto' : 'unset'),
    borderRight: `1px solid ${theme.components.sidebar.border}`,
    '& ul': {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
  },
  secondaryPane: {
    gridArea: 'nav-secondary-pane',
    paddingTop: 16,
    gap: 8,

    '& > h6': {
      marginLeft: 16,
    },
  },
  navText: {
    display: 'grid',
    width: '100%',
    gridAutoFlow: 'column',
    gridTemplateColumns: '72px 1fr',
  },
  textOnlyNavItem: {
    paddingLeft: 32,
  },
  version: {
    margin: theme.spacing(1, 0),
    color: theme.components.sidebar.text,
    textAlign: 'center',
  },
}))

export default Sidebar
